import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import ButtonIconH from "../../components/ButtonIconH";
import DataTable from "react-data-table-component";
import NavTableCell from "./NavTableCell";
import { userData } from "../../js/utils";
import { post } from "../../js/post";
import "../../css/Extras.css";

const HealthData = forwardRef(function HealthData(
  {
    createPopupQuestion,
    removePopupQuestion,
    lines,
    onAccept,
    onClose,
    mode = "sale",
  },
  ref,
) {
  const saleMode = mode === "sale";
  const cardMode = mode === "card";
  const savedListRef = useRef("[]");
  const [list, setList] = useState([]);

  const getHealthData = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const listN = lines.map((line) => {
      const fechaCompra =
        line.line.healthControl.date || (saleMode ? today : "");
      return {
        idDatoSanitario: line.id,
        fechaCompra,
        codigoArticulo: line.line.code,
        lastQueriedArticulo: line.line.code,
        descripcion: line.line.healthControl.description,
        nAlbaran: line.line.healthControl.deliveryNote,
        nExpedicion: line.line.healthControl.shippingNumber,
        nLote: line.line.healthControl.lotNumber,
        validated: line.line.code !== "",
      };
    });
    setList(listN);
    savedListRef.current = JSON.stringify(listN);

    if (listN.length > 0 && saleMode) {
      setTimeout(() => {
        const el = document.getElementById("ntc-hd-1-3");
        if (el) {
          el.focus();
          el.select();
        }
      }, 100);
    }
  };

  const updateListField = (index, field, value) => {
    const listN = structuredClone(list);
    listN[index][field] = value;
    listN[index].validated = listN[index].codigoArticulo !== "";
    setList(listN);
  };

  const onBlurArticulo = async (index, value) => {
    if (value === "" || value === list[index]?.lastQueriedArticulo) {
      return;
    }

    const response = await post("BuscarProducto.php", {
      token: userData.token,
      input: value,
      soloOPTIWIN: true,
    });

    setList((prev) => {
      const listN = structuredClone(prev);
      listN[index].codigoArticulo = response.codigoOPTIWIN;
      listN[index].lastQueriedArticulo = response.codigoOPTIWIN;
      listN[index].validated = response.codigoOPTIWIN !== "";
      return listN;
    });

    if (response.codigoOPTIWIN === "") {
      createPopupQuestion(
        `Código no encontrado en almacén.`,
        () => removePopupQuestion(),
        null,
        "Cerrar",
      );
    }
  };

  const focusNextCell = (row, col) => {
    let nextRow = row;
    let nextCol = col;

    for (let attempts = 0; attempts < 100; attempts++) {
      nextCol += 1;
      if (nextCol > 6) {
        nextCol = 1;
        nextRow += 1;
      }

      const el = document.getElementById(`ntc-hd-${nextRow}-${nextCol}`);
      if (!el) {
        return;
      }
      if (!el.readOnly && !el.disabled) {
        el.focus();
        if (el.select) {
          el.select();
        }
        return;
      }
    }
  };

  const addNewRow = () => {
    const today = new Date().toISOString().slice(0, 10);
    setList((prev) => [
      ...prev,
      {
        idDatoSanitario: -1,
        fechaCompra: today,
        codigoArticulo: "",
        lastQueriedArticulo: "",
        descripcion: "",
        nAlbaran: "",
        nExpedicion: "",
        nLote: "",
        validated: false,
      },
    ]);
  };

  const removeRow = (index) => {
    createPopupQuestion(
      `¿Desea eliminar el dato sanitario?`,
      () => {
        setList((prev) => prev.filter((_, i) => i !== index));
        removePopupQuestion();
      },
      () => removePopupQuestion(),
      "Sí",
      "No",
    );
  };

  const healthDataColumns = useMemo(
    () => [
      {
        name: "Fecha",
        width: "14%",
        cell: (row, index) => (
          <div className="ntc-cell-header">
            <div className="navtablecell">
              <input
                id={"ntc-hd-" + (index + 1) + "-1"}
                type="date"
                max="9999-12-31"
                value={row.fechaCompra}
                onChange={(e) =>
                  updateListField(index, "fechaCompra", e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    focusNextCell(index + 1, 1);
                  }
                }}
                readOnly={cardMode}
                disabled={cardMode}
              />
            </div>
          </div>
        ),
      },
      {
        name: "Artículo",
        width: "14%",
        cell: (row, index) => (
          <div className="ntc-cell-header">
            <NavTableCell
              id={"ntc-hd-" + (index + 1) + "-2"}
              value={row.codigoArticulo}
              type=""
              canBeEmpty={true}
              onChange={(value) =>
                updateListField(index, "codigoArticulo", value)
              }
              onBlur={(value) => onBlurArticulo(index, value)}
              onPressEnter={() => focusNextCell(index + 1, 2)}
              readOnly={saleMode || cardMode}
              maxLength={10}
              stock=""
            />
          </div>
        ),
      },
      {
        name: "Descripción",
        width: mode === "patient" ? "25%" : "30%",
        cell: (row, index) => (
          <div className="ntc-cell-header">
            <NavTableCell
              id={"ntc-hd-" + (index + 1) + "-3"}
              value={row.descripcion}
              type=""
              canBeEmpty={true}
              onChange={(value) => updateListField(index, "descripcion", value)}
              onPressEnter={() => focusNextCell(index + 1, 3)}
              readOnly={cardMode}
              maxLength={100}
              stock=""
            />
          </div>
        ),
      },
      {
        name: "Albarán",
        width: "14%",
        cell: (row, index) => (
          <div className="ntc-cell-header">
            <NavTableCell
              id={"ntc-hd-" + (index + 1) + "-4"}
              value={row.nAlbaran}
              type=""
              canBeEmpty={true}
              onChange={(value) => updateListField(index, "nAlbaran", value)}
              onPressEnter={() => focusNextCell(index + 1, 4)}
              readOnly={cardMode}
              maxLength={50}
              stock=""
            />
          </div>
        ),
      },
      {
        name: "Expedición",
        width: "14%",
        cell: (row, index) => (
          <div className="ntc-cell-header">
            <NavTableCell
              id={"ntc-hd-" + (index + 1) + "-5"}
              value={row.nExpedicion}
              type=""
              canBeEmpty={true}
              onChange={(value) => updateListField(index, "nExpedicion", value)}
              onPressEnter={() => focusNextCell(index + 1, 5)}
              readOnly={cardMode}
              maxLength={50}
              stock=""
            />
          </div>
        ),
      },
      {
        name: "Lote",
        width: "14%",
        cell: (row, index) => (
          <div className="ntc-cell-header">
            <NavTableCell
              id={"ntc-hd-" + (index + 1) + "-6"}
              value={row.nLote}
              type=""
              canBeEmpty={true}
              onChange={(value) => updateListField(index, "nLote", value)}
              onPressEnter={() => focusNextCell(index + 1, 6)}
              readOnly={cardMode}
              maxLength={50}
              stock=""
            />
          </div>
        ),
      },
      {
        name: "",
        width: mode === "patient" ? "5%" : "0%",
        cell: (row, index) =>
          mode === "patient" && (
            <div className="ntc-cell-header">
              <div className="navtable-opt ntc-trash">
                <img
                  src="/images/remove-blue.svg"
                  onClick={() => removeRow(index)}
                />
              </div>
            </div>
          ),
      },
    ],
    [list, mode, saleMode, cardMode],
  );

  const saveHealthData = async () => {
    const valid = list.reduce((acc, curr) => {
      acc = acc && curr.validated;
      return acc;
    }, true);
    if (!valid) {
      createPopupQuestion(
        saleMode
          ? `Debe introducir un número de artículo.`
          : `Se deben rellenar todos los códigos de artículo antes de guardar.`,
        () => removePopupQuestion(),
        null,
        "Cerrar",
      );
      return false;
    }

    const today = new Date().toISOString().slice(0, 10);
    const hasFutureDate = list.some(
      (curr) => curr.fechaCompra !== "" && curr.fechaCompra > today,
    );
    if (hasFutureDate) {
      createPopupQuestion(
        `La fecha no puede ser superior a la fecha actual.`,
        () => removePopupQuestion(),
        null,
        "Cerrar",
      );
      return false;
    }

    const success = await onAccept(list);
    if (success) {
      savedListRef.current = JSON.stringify(list);
    }
    return success;
  };

  const onClickAccept = async () => {
    const success = await saveHealthData();
    if (success) {
      createPopupQuestion(
        `Datos sanitarios guardados de manera correcta.`,
        () => removePopupQuestion(),
        null,
        "Aceptar",
      );
    }
  };

  const handleRequestClose = () => {
    if (JSON.stringify(list) === savedListRef.current) {
      onClose();
      return;
    }

    createPopupQuestion(
      `Existen cambios sin guardar. ¿Desea guardar estos cambios antes de cerrar?`,
      async () => {
        removePopupQuestion();
        const success = await saveHealthData();
        if (success) {
          createPopupQuestion(
            `Datos sanitarios guardados de manera correcta.`,
            () => {
              removePopupQuestion();
              onClose();
            },
            null,
            "Aceptar",
          );
        }
      },
      () => {
        removePopupQuestion();
        onClose();
      },
      "Sí",
      "No",
      () => removePopupQuestion(),
      "Cancelar",
    );
  };

  useImperativeHandle(ref, () => ({
    requestClose: handleRequestClose,
  }));

  useEffect(() => {
    getHealthData();
  }, [lines]);

  return (
    <div className="extra-container">
      <div className="extra-container-info hd-table">
        <div className="informacion-sanitaria-table sales-table sales-table-item">
          {saleMode && (
            <span>
              Rellene la información sanitaria para cada uno de los productos
              que se listan a continuación.
            </span>
          )}
          <div style={saleMode ? null : { marginTop: 20 }}>
            <DataTable
              columns={healthDataColumns}
              data={list}
              className="custom-datatable navtable"
              noDataComponent="No existen datos sanitarios."
              customStyles={{
                table: {
                  style: {
                    backgroundColor: "transparent",
                  },
                },
                noData: {
                  style: {
                    backgroundColor: "transparent",
                  },
                },
              }}
            />
          </div>
          {mode === "patient" && (
            <div className="sales-view-more sales-view-more-options">
              <div className="load-more">
                <img
                  src="/images/load.svg"
                  loading="lazy"
                  width="22"
                  height="22"
                  alt="load"
                  className="vectors-wrapper-76"
                  onClick={addNewRow}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="popup-buttons">
        {mode === "sale" && (
          <ButtonIconH
            text="Continuar venta"
            onClick={onClickAccept}
          ></ButtonIconH>
        )}
        {mode === "patient" && (
          <>
            <ButtonIconH text="Guardar" onClick={onClickAccept}></ButtonIconH>
            <ButtonIconH
              text="Cerrar"
              onClick={handleRequestClose}
            ></ButtonIconH>
          </>
        )}
        {mode === "card" && (
          <ButtonIconH text="Cerrar" onClick={handleRequestClose}></ButtonIconH>
        )}
      </div>
    </div>
  );
});

export default HealthData;
